import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Clock, Coins, ShieldCheck, TrendingUp } from "lucide-react";
import { Eyebrow, Reveal } from "../components/Reveal";
import { WhatsAppIcon } from "../components/icons";
import { ECONOMIA_BENEFICIOS, ECONOMIA_TAREFAS, waLink } from "../data/site";

const ICONS = { Clock, Coins, ShieldCheck, TrendingUp };

const Counter = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / 1400, 1);
      setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);
  return <span ref={ref}>{val}{suffix}</span>;
};

const TaskRow = ({ task, index }) => (
  <li className="grid grid-cols-[1fr_auto] items-center gap-4 py-4" data-testid={`economia-tarefa-${index}`}>
    <div>
      <p className="text-sm font-medium text-slate-200">{task.nome}</p>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06]">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${task.pct}%` }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 1.1, delay: 0.15 * index, ease: [0.22, 1, 0.36, 1] }}
          className="h-full rounded-full bg-gradient-to-r from-[#007BFF] to-[#3a9bff]"
        />
      </div>
    </div>
    <span className="text-right text-xs text-slate-400">
      <span className="block text-base font-semibold text-[#3a9bff]">{task.horas}h</span>
      por semana
    </span>
  </li>
);

export const Economize = () => {
  const total = ECONOMIA_TAREFAS.reduce((s, t) => s + t.horas, 0);
  return (
    <section id="economize" data-testid="economize-section" className="relative scroll-mt-24 overflow-hidden bg-[#05070D]/65 py-28 lg:py-36">
      <div className="pointer-events-none absolute -left-40 top-1/3 h-[420px] w-[420px] rounded-full bg-[#007BFF]/10 blur-[120px]" />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-start lg:px-10">
        <div>
          <Reveal>
            <Eyebrow>03 · Automatize e economize</Eyebrow>
            <h2 className="mt-5 text-3xl font-semibold tracking-tight text-slate-100 sm:text-4xl lg:text-5xl" data-testid="economize-heading">
              Automatize e <span className="text-[#3a9bff]">economize</span>.
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400" data-testid="economize-intro">
              Toda empresa tem tarefas que se repetem todos os dias: responder as mesmas
              perguntas, copiar dados de um sistema para outro, lembrar clientes, emitir
              relatórios. Cada uma parece pequena, mas juntas consomem horas da equipe e
              abrem espaço para erros. Automação de processos é fazer o software executar
              esse trabalho repetitivo, com regras claras e sem cansar, para que as pessoas
              foquem no que realmente gera receita.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-4 sm:grid-cols-2" data-testid="economize-beneficios">
            {ECONOMIA_BENEFICIOS.map((b, i) => {
              const Icon = ICONS[b.icon];
              return (
                <Reveal key={b.title} delay={0.08 * i}>
                  <div className="group h-full rounded-2xl border border-white/[0.07] bg-[#0d1219]/80 p-6 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-[#007BFF]/40" data-testid={`beneficio-${i}`}>
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#007BFF]/25 bg-[#007BFF]/10 text-[#3a9bff] transition-transform duration-500 group-hover:scale-110">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-5 text-base font-semibold text-slate-100">{b.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{b.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.15} className="lg:sticky lg:top-28">
          <div className="relative overflow-hidden rounded-2xl border border-[#007BFF]/25 bg-gradient-to-br from-[#0a1830] via-[#0B0F14] to-[#05070D] p-8" data-testid="economia-painel">
            <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#007BFF]/20 blur-[80px]" />
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500">Exemplo ilustrativo</p>
            <p className="mt-3 text-sm text-slate-400">Horas de trabalho manual que uma pequena empresa costuma gastar por semana e que podem ser automatizadas:</p>
            <ul className="mt-4 divide-y divide-white/[0.06]">
              {ECONOMIA_TAREFAS.map((t, i) => <TaskRow key={t.nome} task={t} index={i} />)}
            </ul>
            <div className="mt-6 flex flex-col gap-3 rounded-xl border border-[#007BFF]/30 bg-[#007BFF]/10 px-5 py-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#6db4ff]">Tempo recuperado</p>
                <p className="mt-1 whitespace-nowrap text-3xl font-semibold text-slate-50" data-testid="economia-total">
                  <Counter to={total} suffix="h" /> <span className="text-base font-normal text-slate-400">/ semana</span>
                </p>
              </div>
              <p className="text-xs text-slate-400 sm:text-right">
                ≈ <span className="font-semibold text-slate-200"><Counter to={total * 4} suffix="h" /></span> por mês
                <br />de volta para o negócio
              </p>
            </div>
            <a
              href={waLink("Olá! Quero descobrir quanto tempo e dinheiro minha empresa pode economizar com automação de processos.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="economize-whatsapp-cta"
              className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#007BFF] px-7 py-3.5 text-sm font-semibold text-white transition-shadow duration-300 hover:shadow-[0_0_36px_rgba(0,123,255,0.55)]"
            >
              <WhatsAppIcon className="h-4 w-4" />
              Quanto minha empresa pode economizar?
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
